import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { modelClass, previewFleetMembers, setFleetMode, updateFleet } from '/imports/api/fleets';
import { botModes, type BotMode } from '/imports/api/bots';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Button } from '/imports/ui/Button';
import { Select } from '/imports/ui/Select';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { Input } from '/imports/ui/Input';
import { TextArea } from '/imports/ui/TextArea';
import { NumberInput } from '/imports/ui/NumberInput';
import { CardListPicker } from '/imports/ui/CardListPicker';
import { InventoryPolicyPicker } from '/imports/ui/InventoryPolicyPicker';
import { InheritToggle } from '/imports/ui/InheritToggle';

/**
 * Live membership preview for the prefix being typed.
 *
 * A prefix is easy to get subtly wrong -- `MogBot` also captures
 * MogBotsSelling, and one character short can swallow a neighbouring family.
 * Showing the resulting membership (and flagging bots pulled out of another
 * fleet) turns a save from a guess into a decision.
 */
const MemberPreview = ({ prefix, fleetId, storedExclusions }: {
  prefix: string; fleetId?: string; storedExclusions?: string[];
}) => {
  const [state, setState] = useState<{
    members: { username: string; mode: string; stolenFrom?: string }[];
    excluded: { username: string; mode: string }[];
    total: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  // The exclusion list as last written; the document prop only refreshes on a
  // full page reload, so toggles keep their own truth after the first click.
  const [exclusions, setExclusions] = useState<string[] | null>(null);
  const [nonce, setNonce] = useState(0);
  const effectiveExclusions = exclusions ?? storedExclusions ?? [];

  useEffect(() => {
    const clean = (prefix || '').trim();
    if (!clean) { setState(null); return; }
    let cancelled = false;
    setBusy(true);
    // Debounced: this runs on every keystroke of the prefix field.
    const t = setTimeout(async () => {
      try {
        const res = await previewFleetMembers.call({ prefix: clean, fleetId });
        if (!cancelled) setState(res);
      }
      catch { if (!cancelled) setState(null); }
      finally { if (!cancelled) setBusy(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [prefix, fleetId, nonce]);

  const toggle = async (username: string, exclude: boolean) => {
    if (!fleetId) return;
    const next = exclude
      ? [...effectiveExclusions.filter((u) => u.toLowerCase() !== username.toLowerCase()), username]
      : effectiveExclusions.filter((u) => u.toLowerCase() !== username.toLowerCase());
    try {
      await updateFleet.call({ _id: fleetId, excludedBots: next } as any);
      setExclusions(next);
      setNonce((n) => n + 1);
      toast.success(exclude ? `${username} excluded from fleet` : `${username} added back to fleet`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };

  if (!prefix?.trim()) {
    return <div className="text-caption text-fg-3">Enter a prefix to see which bots it captures.</div>;
  }
  if (busy && !state) return <div className="text-caption text-fg-3">Checking…</div>;
  if (!state) return null;

  const stolen = state.members.filter((m) => m.stolenFrom);
  return (
    <div className="rounded-card border border-soft bg-panel-alt p-3">
      <div className="eyebrow text-fg-3">
        {state.total} bot{state.total === 1 ? '' : 's'} in this fleet
      </div>
      {state.total === 0 && (
        <div className="mt-1 text-caption text-warn">
          No bots match this prefix. Check the spelling and capitalisation.
        </div>
      )}
      {stolen.length > 0 && (
        <div className="mt-1 text-caption text-warn">
          {stolen.length} would move here from another fleet: {stolen.map((m) => `${m.username} (${m.stolenFrom})`).join(', ')}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        {state.members.slice(0, 60).map((m) => (
          <span key={m.username} className="inline-flex items-center gap-1 rounded-full border border-mid px-2 py-0.5 font-mono text-[11px] text-fg-2">
            {m.username}
            {fleetId && (
              <button
                type="button"
                title="Remove from fleet"
                className="cursor-pointer leading-none text-fg-3 hover:text-warn"
                onClick={() => void toggle(m.username, true)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {state.total > 60 && <span className="text-caption text-fg-3">+{state.total - 60} more</span>}
      </div>
      {state.excluded.length > 0 && (
        <div className="mt-3 rounded-card border border-soft bg-panel p-3">
          <div className="eyebrow text-fg-3">
            {state.excluded.length} bot{state.excluded.length === 1 ? '' : 's'} excluded from this fleet
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {state.excluded.map((m) => (
              <span key={m.username} className="inline-flex items-center gap-1 rounded-full border border-mid px-2 py-0.5 font-mono text-[11px] text-fg-3">
                {m.username}
                <button
                  type="button"
                  title="Add to fleet"
                  className="cursor-pointer leading-none text-fg-3 hover:text-primary"
                  onClick={() => void toggle(m.username, false)}
                >
                  +
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Bulk mode change for the fleet's bots.
 *
 * The mode is applied by the confirm button rather than by the Select's own
 * change event: this rewrites every member bot, and a select fires on arrow
 * keys as well as clicks, so committing on change would let a stray keypress
 * re-mode the whole fleet with no way back. The count in the button is the
 * point -- it is the operator's last look at how much this touches.
 */
const SetModeModal = ({ fleet, onClose }: { fleet: any; onClose(): void }) => {
  // No default mode. Every value in the list is a real, destructive choice, so
  // the operator picks one rather than confirming whichever happened to sort
  // first.
  const [mode, setMode] = useState<BotMode | ''>('');
  const [members, setMembers] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await previewFleetMembers.call({ prefix: String(fleet?.prefix ?? ''), fleetId: fleet?._id });
        if (!cancelled) setMembers(res.total);
      }
      catch { if (!cancelled) setMembers(null); }
    })();
    return () => { cancelled = true; };
  }, [fleet?._id, fleet?.prefix]);

  const apply = async () => {
    if (!mode) return;
    setBusy(true);
    try {
      const res = await setFleetMode.call({ fleetId: fleet._id, mode });
      if (!res.matched) toast.info('No bots match this fleet\'s prefix');
      else if (!res.updated) toast.info(`All ${res.matched} bot${res.matched === 1 ? '' : 's'} were already ${mode}`);
      else toast.success(`Set ${res.updated} bot${res.updated === 1 ? '' : 's'} to ${mode}`);
      // Mode is read once at startup, so a running bot keeps its old one. Said
      // out loud, because otherwise the change looks complete and is not.
      if (res.running.length) {
        toast.warn(
          `${res.running.length} of them ${res.running.length === 1 ? 'is' : 'are'} running and will keep their current mode until restarted: `
          + res.running.slice(0, 8).join(', ') + (res.running.length > 8 ? `, +${res.running.length - 8} more` : ''),
          { autoClose: 10000 },
        );
      }
      onClose();
    }
    catch (e: any) {
      toast.error(`${e}`);
    }
    finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmModal
      title="Set mode for every bot in this fleet"
      body={(
        <div>
          <div className="mb-2">
            {members === null
              ? 'Checking which bots this fleet captures…'
              : `${members} bot${members === 1 ? '' : 's'} match this fleet's prefix.`}
          </div>
          <Select
            value={mode}
            options={[{ value: '', label: 'Choose a mode…' }, ...botModes.map((m) => ({ value: m, label: m }))]}
            onChange={(v) => setMode(v as BotMode | '')}
          />
        </div>
      )}
      confirmText={
        busy ? 'Setting…'
          : !mode ? 'Choose a mode'
            : `Set ${members === null ? 'all' : members} to ${mode}`
      }
      onConfirm={() => { if (!busy) void apply(); }}
      onClose={onClose}
    />
  );
};

export const FleetPage = () => {
  const [document, setDocument] = useState<any>();
  const [settingMode, setSettingMode] = useState(false);
  return (
    <ModelFormPage
      modelClass={modelClass as any}
      permission={(user) => !!user?.isSuperUser}
      setDocument={setDocument as any}
      extraButtons={document?._id ? (
        <>
          <Button type="button" className="btn secondary" onClick={() => setSettingMode(true)}>Set Mode</Button>
          {settingMode && <SetModeModal fleet={document} onClose={() => setSettingMode(false)} />}
        </>
      ) : null}
      formSpec={() => [
        [{ field: 'name', label: 'Fleet Name', input: { type: Input } }],
        [{
          field: 'prefix', label: 'Username Prefix', input: { type: Input },
          tip: 'Bots whose username starts with this belong to the fleet. Case-insensitive. '
          + 'If two fleets both match, the longer prefix wins, so you can carve out a special case later.',
        }],
        // A render-only row: the input callback gets the live form state, so the
        // preview follows what is typed rather than what is saved.
        [{
          field: 'prefix',
          input: (_setUpdate, update, document) => (
            <MemberPreview
              prefix={String((update as any)?.prefix ?? (document as any)?.prefix ?? '')}
              fleetId={(document as any)?._id}
              storedExclusions={((document as any)?.excludedBots ?? []) as string[]}
            />
          ),
        }],
        [{
          field: 'classifiedCardListId',
          input: (setUpdate, update, document) => (
            <div className="json-field">
              <label>Classified Card List</label>
              <div className="mb-1 text-caption text-fg-3">Which cards this fleet advertises. Edit the list to change every fleet using it; blank inherits Settings.</div>
              <CardListPicker
                value={'classifiedCardListId' in ((update ?? {}) as object) ? (update as any).classifiedCardListId : (document as any)?.classifiedCardListId}
                onChange={(id) => setUpdate((prev: any) => ({ ...prev, classifiedCardListId: id }))}
                inheritLabel="Inherit from Settings"
              />
            </div>
          ),
        }],
        [{
          field: 'classified', label: 'Classified Template', input: { type: TextArea },
          tip: 'Ad template for every bot in this fleet; {} slots are filled from the classified card list. '
          + 'A bot\'s own config.classified overrides this. Blank falls through to the mode-generated ad.',
        }],
        [{
          field: 'buyCardListId',
          input: (setUpdate, update, document) => (
            <div className="json-field">
              <label>Buy Card List</label>
              <div className="mb-1 text-caption text-fg-3">What this fleet&apos;s buy/hybrid bots BUY. Blank inherits Settings; with neither, legacy Buy Targets govern.</div>
              <CardListPicker
                value={'buyCardListId' in ((update ?? {}) as object) ? (update as any).buyCardListId : (document as any)?.buyCardListId}
                onChange={(id) => setUpdate((prev: any) => ({ ...prev, buyCardListId: id }))}
                inheritLabel="Inherit from Settings"
              />
            </div>
          ),
        }],
        [{
          field: 'buyCap', label: 'Buy Cap', input: { type: NumberInput },
          tip: 'Max copies of any bought card to OWN (the old per-target buycap). Blank inherits Settings.buyCap, then 4. The separate 4-per-trade throttle still applies.',
        }],
        [{
          field: 'sellCardListId',
          input: (setUpdate, update, document) => (
            <div className="json-field">
              <label>Sell Card List</label>
              <div className="mb-1 text-caption text-fg-3">What sell/hybrid bots stock and sell. Price floors live inside the list query.</div>
              <CardListPicker
                value={'sellCardListId' in ((update ?? {}) as object) ? (update as any).sellCardListId : (document as any)?.sellCardListId}
                onChange={(id) => setUpdate((prev: any) => ({ ...prev, sellCardListId: id }))}
                inheritLabel="Inherit from Settings"
              />
            </div>
          ),
        }],
        [{
          field: 'inventoryPolicyId',
          input: (setUpdate, update, document) => (
            <div className="json-field">
              <label>Inventory Policy</label>
              <div className="mb-1 text-caption text-fg-3">
              Per-card stock quantities for this fleet’s sell/hybrid bots. Membership still comes from the sell selection — a policy only refines how many copies to hold.
              </div>
              <InventoryPolicyPicker
                value={'inventoryPolicyId' in ((update ?? {}) as object) ? (update as any).inventoryPolicyId : (document as any)?.inventoryPolicyId}
                onChange={(id) => setUpdate((prev: any) => ({ ...prev, inventoryPolicyId: id }))}
                inheritLabel="No policy — sellCap/stocksize"
              />
            </div>
          ),
        }],
        [{
          field: 'replenishPriority', label: 'Replenish Priority', input: { type: NumberInput },
          tip: 'Higher number = this fleet\'s bots replenish first at the shared depots. '
          + 'A bot\'s own config.replenishPriority overrides this; blank inherits Settings, then 2.',
        }],
        [{
          field: 'defaultBuyPrice', label: 'Default Buy Price (junk floor)', input: { type: NumberInput },
          tip: 'Floor price for cards with no real buy price, for this fleet. '
          + 'A bot\'s own config.defaultBuyPrice overrides this; blank inherits Settings.',
        }],
        [{
          field: 'defaultSellPrice', label: 'Default Sell Price (junk floor)', input: { type: NumberInput },
          tip: 'Floor price for selling junk (cards with neither real price), for this fleet. '
          + 'A bot\'s own config.defaultSellPrice overrides this; blank inherits Settings.',
        }],
        [{
          field: 'overdraft',
          input: (setUpdate, update, document) => (
            <InheritToggle
              label="Overdraft (buy on credit)"
              hint="Whether this fleet's bots keep buying once their tickets run out, settling the shortfall as store credit. A bot's own config.overdraft overrides this."
              value={'overdraft' in ((update ?? {}) as object) ? (update as any).overdraft : (document as any)?.overdraft}
              onChange={(v) => setUpdate((prev: any) => ({ ...prev, overdraft: v }))}
              inheritLabel="Inherit from Settings"
              yesLabel="Yes — buy past the ticket balance"
              noLabel="No — stop when tickets run out"
            />
          ),
        }],
        [{
          field: 'sellCap', label: 'Sell Cap', input: { type: NumberInput },
          tip: 'Stock level to hold per sold card (the old sellcap). Blank inherits Settings.sellCap, then the bot\'s own stocksize option.',
        }],

      ]}
    />
  );
};
